import { useState } from "react";
import { ScrollView } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Button } from "@src/components/Button";
import { Icon } from "@src/components/Icon";
import { sizes } from "@src/constants/sizes";
import { SupportAccessRestricted } from "@src/components/web/SupportAccessRestricted.web";
import { FullScreenLoading } from "@src/components/screen-state";
import { showErrorToast, showSuccessToast } from "@src/utils/toast";
import { useSupportUserStatus, useTenantId } from "@src/features/tenants";
import { track } from "@visioni/a3s-analytics";
import { cameraScope } from "@src/features/cameras/analytics";
import { requestErrorType } from "@src/services/api/error-type";
import { useBooleanFlag, FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";
import {
  useCameraTestSignalLog,
  useLatestCameraTestSignals,
  useRefreshCameraTestSignalLog,
  useTriggerTestSignal,
} from "@visioni/a3s-monitoring/app";
import type { TestSignalPath } from "@visioni/a3s-monitoring/app";
import { resolveTestSignalsAccess } from "../access";
import { TEST_SIGNAL_ACTIONS } from "../actions";
import { useTestSignalCooldown } from "../useTestSignalCooldown";
import { signalPathColors } from "../signal-path-colors";
import { AlarmTestSignalFailed, AlarmTestSignalSent } from "../analytics";
import { TestSignalLog } from "./TestSignalLog";
import { TestSignalSummary } from "./TestSignalSummary";
import { TestSignalWatchStatus } from "./TestSignalWatchStatus";

/** Caps the caption under each button so the pair stays side by side. */
const SEND_COLUMN_MAX_WIDTH = 320;
const SEND_ICON_SIZE = 16;

interface CameraTestSignalsProps {
  siteId: string;
  cameraKey: string;
}

/**
 * Support-only per-camera test signal triggers, and the log they land in.
 *
 * The trigger API can only report that the command was accepted for delivery to
 * the device, so the answer to "did it work?" comes from the log: a press reads
 * the log, sends, then watches for the signal it produces and reports what came
 * back.
 */
export function CameraTestSignals({ siteId, cameraKey }: CameraTestSignalsProps) {
  const { theme } = useUnistyles();
  const supportStatus = useSupportUserStatus();
  const tenantId = useTenantId();
  const uiFlagEnabled = useBooleanFlag(FEATURE_FLAGS.TEST_SIGNAL_UI);
  const trigger = useTriggerTestSignal();
  const subject = `${siteId}#${cameraKey}`;
  const cooldown = useTestSignalCooldown(subject);
  /**
   * The camera a press is in flight for, covering the log read and the command
   * together — see the buttons' `disabled`.
   *
   * Names the camera rather than being a bare flag: the page is reused across
   * cameras when only the route params change, so a switch mid-press would
   * otherwise leave the camera just arrived at with its buttons held.
   */
  const [firingFor, setFiringFor] = useState<string | null>(null);
  const refreshLog = useRefreshCameraTestSignalLog();

  const access = resolveTestSignalsAccess(supportStatus, uiFlagEnabled);
  const log = useCameraTestSignalLog({ siteId, cameraKey, enabled: access === "granted" });
  const summary = useLatestCameraTestSignals({
    siteId,
    cameraKey,
    loaded: log.entries,
    enabled: access === "granted",
  });

  if (access === "pending") {
    return <FullScreenLoading message="Checking access..." />;
  }
  if (access === "denied") {
    return <SupportAccessRestricted />;
  }

  const fire = async (signalPath: TestSignalPath) => {
    setFiringFor(subject);
    try {
      // Read the log *before* sending, so the watch measures against what was
      // there rather than against whatever the page is showing — which may be
      // a cache written up to thirty days ago. This is what lets the watermark
      // be trusted; without it a signal that arrived while the page was away
      // looks like an answer to this press.
      const readAtPress = await log.readLog();

      // No read, no command. Sending anyway would put real traffic on a live
      // ARC and then be unable to say whether it arrived — and a watch armed
      // against a log we could not read would count the first manual signal it
      // ever saw and report a pass for a signal nobody sent. Better to send
      // nothing and say so.
      if (readAtPress === null) {
        showErrorToast({
          message: "Could not read the test signal log",
          description: "The command was not sent. Try again in a moment.",
        });
        return;
      }

      const scope = cameraScope(tenantId, siteId, cameraKey);
      try {
        await trigger.mutateAsync({ siteId, cameraKey, signalPath });
      } catch (error: unknown) {
        track(AlarmTestSignalFailed, { ...scope, signal_path: signalPath, error_type: requestErrorType(error) });
        throw error;
      }
      track(AlarmTestSignalSent, { ...scope, signal_path: signalPath });
      cooldown.markFired(signalPath);
      log.noteTriggered(signalPath, readAtPress);

      // The operator's own command should appear at once. The *signal* will
      // not — that takes a minute or two, which the watch covers.
      refreshLog(siteId, cameraKey);

      // Deliberately not "test signal fired": all that is known here is that
      // the command was accepted for delivery to the device.
      showSuccessToast({ message: "Command sent to the camera" });
    } catch (error: unknown) {
      showErrorToast({ error });
    } finally {
      // Release only our own lock. After a mid-press camera switch a second
      // press takes it for the new camera, and clearing that here would
      // re-enable its buttons while its read and command are still in flight —
      // the race this lock exists to stop.
      setFiringFor((current) => (current === subject ? null : current));
    }
  };

  return (
    <ScrollView
      contentContainerStyle={{
        // Left-aligned, but capped like the reports pages rather than running
        // the full width of a large display — `maxWidth` without
        // `alignItems: "center"` is what gives both.
        padding: theme.spacing["6xl"],
        gap: theme.spacing["3xl"],
        maxWidth: sizes.webContentMaxWidth,
      }}
    >
      <View gap="3xl">
        <View
          bg="surfaceCardInner"
          borderRadius={theme.spacing.lg}
          borderWidth={1}
          borderColor="borderCard"
          p="3xl"
          gap="md"
        >
          <Text textStyle="sm/semibold" color="text">
            What is a test signal?
          </Text>
          <Text textStyle="sm/regular" color="textMuted">
            A test signal is a message from the camera (usually with a clip) that allows us to diagnose issues and check
            the functionality of the device. Test signals are automatically triggered on most devices. For example, a
            camera might call home every day. We can also trigger them manually.
          </Text>
          <Text textStyle="sm/semibold" color="text">
            What a test signal checks
          </Text>
          <Text textStyle="sm/regular" color="textMuted">
            A test signal exercises the whole chain up to a3s cloud: the camera and its recording, the commsboard&apos;s
            a3s edge agent, network connectivity, clip upload, and the monitoring pipeline. Whether it travels further
            depends on who monitors the asset: test signals for vision-monitored assets will be delivered to Patriot as
            well; other monitoring providers will not receive test signals.
          </Text>
          <Text textStyle="sm/semibold" color="text">
            What it doesn&apos;t
          </Text>
          <Text textStyle="sm/regular" color="textMuted">
            It won&apos;t tell you whether detection is tuned to catch a real intrusion, whether the camera&apos;s alarm
            schedule is set up correctly, or whether Patriot&apos;s action plan handles a real alarm the way you expect.
            In Patriot it arrives as a test signal (code 102), logged rather than actioned as an alarm.
          </Text>
          <Text textStyle="sm/semibold" color="text">
            How to use them
          </Text>
          <Text textStyle="sm/regular" color="textMuted">
            Press a button below to send a test signal command. The log will then watch for the signal it produces,
            which takes a minute or two to arrive. If nothing arrives, there are issues somewhere in the chain and it
            may require intervention.
          </Text>
        </View>

        <View gap="xl">
          <Text textStyle="sm/semibold" color="text">
            Send Test Signal
          </Text>
          {/* `alignItems="flex-start"` keeps each button at its own width; left
              to stretch, a button reads as a banner rather than a control. */}
          <View flexDirection="row" flexWrap="wrap" gap="3xl">
            {TEST_SIGNAL_ACTIONS.map((action) => {
              // `firing` spans the whole of `fire()`, not just the mutation:
              // the log read runs first, and `trigger.isPending` is still
              // false during it, so gating on that alone would let a second
              // press start its own read-and-send underneath.
              const disabled = firingFor === subject || cooldown.isCoolingDown(action.signalPath);
              const colors = signalPathColors(action.signalPath, theme);
              return (
                <View
                  key={action.signalPath}
                  style={{ maxWidth: SEND_COLUMN_MAX_WIDTH }}
                  alignItems="flex-start"
                  gap="sm"
                >
                  <Button
                    label={action.title}
                    variant="primary"
                    size="sm"
                    // The leg's colour, matching its rows in the log below.
                    // Only while enabled: `style` is applied after the
                    // Button's own disabled look and would otherwise hide it.
                    {...(!disabled && {
                      style: { backgroundColor: colors.fill, borderColor: colors.fill },
                      textColor: colors.onFill,
                    })}
                    disabled={disabled}
                    onPress={() => void fire(action.signalPath)}
                    iconLeft={
                      <Icon
                        name="send"
                        size={SEND_ICON_SIZE}
                        color={disabled ? theme.colors.white.default : colors.onFill}
                      />
                    }
                  />
                  <Text textStyle="xs/regular" color="textMuted">
                    {action.subtitle}
                  </Text>
                </View>
              );
            })}
          </View>
        </View>

        <TestSignalWatchStatus watching={log.watching} reports={log.watchReports} onDismiss={log.dismissWatchReport} />

        <TestSignalSummary summary={summary} />

        <TestSignalLog log={log} />
      </View>
    </ScrollView>
  );
}
